import { Refine, Authenticated } from "@refinedev/core";
import routerBindings from "@refinedev/react-router";
import authProvider from "./providers/authProvider";
import dataProvider from "./providers/dataProvider";
import { ThemedLayout, useNotificationProvider } from "@refinedev/antd";
import { ConfigProvider, App as AntApp } from "antd";
import { BrowserRouter, Outlet, Route, Routes, Navigate } from "react-router-dom";
import { AIToolsList } from "./pages/aiTools/list";
import { AIToolCreate } from "./pages/aiTools/create";
import { AIToolEdit } from "./pages/aiTools/edit";
import { AIToolShow } from "./pages/aiTools/show";
import { AuctionLookup } from "./pages/auctions/list";
import { AuctionEdit } from "./pages/auctions/edit";
import { FilterCopyList } from "./pages/filterCopy/list";
import { FiltersList } from "./pages/filters/list";
import { FilterEdit } from "./pages/filters/edit";
import { UserSearchesList } from "./pages/userSearches/list";
import { ClientsAtRiskList } from "./pages/clientsAtRisk/list";
import { SubscriptionsList } from "./pages/subscriptions/list";
import { PurchasesList } from "./pages/purchases/list";
import { ConversionsList } from "./pages/conversions/list";
import { FunnelList } from "./pages/funnel/list";
import { ScraperDiagnosticsList } from "./pages/scraperDiagnostics/list";
import { Login } from "./pages/auth/login";
import { AuditList } from "./pages/audit/list";
import { NotificationsDashboard } from "./pages/notifications/dashboard";
import { NotificationDeliveriesList } from "./pages/notifications/deliveries";
import { NotificationSubscriptionsHealth } from "./pages/notifications/subscriptions";
import { UsersList } from "./pages/users/list";
import { AdminSider } from "./components/layout/AdminSider";
import { AdminTitle } from "./components/layout/AdminTitle";
import { readSiderCollapsed, writeSiderCollapsed } from "./components/layout/siderStorage";
import { resources } from "./resources";

import "@refinedev/antd/dist/reset.css";

function RefineApp() {
  const notificationProvider = useNotificationProvider();
  return (
    <Refine
      dataProvider={dataProvider}
      authProvider={authProvider}
      routerProvider={routerBindings}
      resources={resources}
      notificationProvider={notificationProvider}
      options={{
        syncWithLocation: true,
        warnWhenUnsavedChanges: true,
      }}
    >
      <Routes>
        <Route path="/login">
          <Route index element={<Login />} />
        </Route>
        <Route
          element={
            <Authenticated key="authenticated-layout" redirectOnFail="/login">
              <ThemedLayout
                Sider={AdminSider}
                Title={AdminTitle}
                initialSiderCollapsed={readSiderCollapsed()}
                onSiderCollapsed={writeSiderCollapsed}
              >
                <Outlet />
              </ThemedLayout>
            </Authenticated>
          }
        >
          <Route path="/ai-tools">
            <Route index element={<AIToolsList />} />
            <Route path="create" element={<AIToolCreate />} />
            <Route path="edit/:id" element={<AIToolEdit />} />
            <Route path="show/:id" element={<AIToolShow />} />
          </Route>
          <Route path="/auctions">
            <Route index element={<AuctionLookup />} />
            <Route path="edit/:id" element={<AuctionEdit />} />
          </Route>
          <Route path="/filters">
            <Route index element={<FiltersList />} />
            <Route path="edit/:id" element={<FilterEdit />} />
          </Route>
          <Route path="/filter-copy" element={<FilterCopyList />} />
          <Route path="/user-searches" element={<UserSearchesList />} />
          <Route path="/clients-at-risk" element={<ClientsAtRiskList />} />
          <Route path="/users" element={<UsersList />} />
          <Route path="/subscriptions" element={<SubscriptionsList />} />
          <Route path="/purchases" element={<PurchasesList />} />
          <Route path="/notifications">
            <Route index element={<NotificationsDashboard />} />
            <Route path="deliveries" element={<NotificationDeliveriesList />} />
            <Route path="subscriptions" element={<NotificationSubscriptionsHealth />} />
          </Route>
          <Route path="/conversions" element={<ConversionsList />} />
          <Route path="/funnel" element={<FunnelList />} />
          <Route path="/scraper" element={<ScraperDiagnosticsList />} />
          <Route path="/audit" element={<AuditList />} />
          <Route path="/" element={<Navigate to="/ai-tools" replace />} />
          <Route path="*" element={<Navigate to="/ai-tools" replace />} />
        </Route>
      </Routes>
    </Refine>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#1F8081",
            borderRadius: 8,
          },
        }}
      >
        <AntApp>
          <RefineApp />
        </AntApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}

export default App;
